import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1667667708185-e80206929c7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHdpbmQlMjBmYXJtJTIwY2luZW1hdGljJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc3MTQ4NTkzMHww&ixlib=rb-4.1.0&q=85";

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  // Background drifts down slowly (parallax) while content drifts up faster
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const scrollToSolutions = () => {
    document.getElementById("solutions")?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const STATS = [
    { value: "GWh", label: "Planned Capacity" },
    { value: "2W-4W", label: "EV Range Target" },
    { value: "BIS/ISO", label: "Compliance-Ready" },
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col overflow-hidden"
    >
      {/* Background Image with Ken Burns + parallax */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0 will-change-transform"
      >
        <div className="ken-burns w-full h-full">
          <img
            src={HERO_BG}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#040D1C] via-[#040D1C]/70 to-[#040D1C]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040D1C] via-transparent to-transparent" />
      </motion.div>

      {/* Hero Glow */}
      <div className="absolute inset-0 hero-glow z-[1]" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-24 md:pt-28 pb-20 md:pb-24 w-full flex-1 flex flex-col will-change-transform"
      >
        {/* Top blue overline + massive heading (hover stroke effect) */}
        <div className="stroke-on-hover cursor-default">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-[#1E96FC] leading-[1] mb-2 md:mb-3"
          >
            Powering the future of
          </motion.h2>

          {/* Massive heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
            className="font-display font-bold uppercase text-white leading-[0.85] tracking-tight"
          >
            <span className="block text-[clamp(2rem,9.5vw,8.4rem)] leading-[0.85] stroke-fill-white">
              Intelligent
            </span>
            <span className="block text-[clamp(2rem,9.5vw,8.4rem)] leading-[0.85] stroke-fill-white">
              Energy
            </span>
          </motion.h1>
        </div>

        {/* Pill badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="mt-6 md:mt-8"
        >
          <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-sm bg-[#1E96FC]/10 border border-[#1E96FC]/30 text-white text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1E96FC] animate-pulse" />
            Under Development · Launching 2027
          </span>
        </motion.div>

        {/* Bottom row: stats+CTAs (left)  body text (right) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-auto pt-12 md:pt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end"
        >
          {/* Left column: stats + CTAs */}
          <div className="lg:col-span-7 flex flex-col gap-6 md:gap-8">
            {/* Stats */}
            <div className="flex gap-6 md:gap-10 lg:gap-14 flex-wrap">
              {STATS.map((stat, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-xl lg:text-2xl font-bold text-[#1E96FC]">
                    {stat.value}
                  </span>
                  <span className="text-xs text-slate-500 uppercase tracking-wider mt-1">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button
                data-testid="hero-explore-btn"
                onClick={scrollToSolutions}
                className="bg-[#1E96FC] text-white hover:bg-[#1E96FC]/90 rounded-sm px-6 md:px-8 py-5 md:py-6 text-xs md:text-sm uppercase tracking-[0.15em] font-bold transition-all duration-200 hover:scale-[1.02] w-full sm:w-auto justify-center min-h-[52px]"
              >
                Explore Solutions
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="hero-contact-btn"
                variant="outline"
                onClick={scrollToContact}
                className="border-slate-600 bg-transparent text-slate-200 hover:border-[#1E96FC] hover:text-[#1E96FC] hover:bg-transparent rounded-sm px-6 md:px-8 py-5 md:py-6 text-xs md:text-sm uppercase tracking-[0.15em] font-bold transition-all duration-200 w-full sm:w-auto justify-center min-h-[52px]"
              >
                Get in Touch
              </Button>
            </div>
          </div>

          {/* Right column: body text */}
          <div className="lg:col-span-5">
            <p className="text-sm md:text-base text-slate-400 leading-relaxed">
              Building a new generation of Battery Energy Storage Systems,
              lithium-ion packs for electric mobility, and custom energy
              solutions for industrial-scale applications.
              <br />
              <span className="text-[#1E96FC]">
                Engineered for reliability. Designed for scale.
              </span>
            </p>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        style={{ opacity: contentOpacity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] text-slate-500 uppercase tracking-[0.3em]">
          Scroll
        </span>
        <ChevronDown className="w-4 h-4 text-slate-500 scroll-indicator" />
      </motion.div>
    </section>
  );
}
